<!-- eslint-disable vue/no-restricted-html-elements -->
<template>
  <div
    class="clickable-list"
    :class="[
      $blockClass(blockClass, 'clickable-list'),
      {
        [$class('active')]: linksActive,
        'clickable-list--has-text-hover-icon': displayIcons,
      }
    ]"
    tabindex="0"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <component
      :is="listType"
      class="clickable-list__list"
    >
      <li
        v-for="(item, index) of links"
        :key="index"
        class="clickable-list__list-item"
        :class="{
          'clickable-list__list-item--space': item.spaceAbove
        }"
      >
        <!-- Move OneTrust cookie settings button from theme.liquid to container -->
        <div
          v-if="item.title === $variable('oneTrust.cookieSettingsKey')"
          js-onetrust-cookie-settings="cookieContainerDesktop"
          :class="[
            {
              [$class('active')]: item.label === selected || item.title === selected
            }
          ]"
          @mouseover="() => handleLinkHover(item)"
        />

        <!-- Move OneTrust cookie settings button from theme.liquid to container  -->
        <div
          v-else-if="item.title === $variable('accessibe.accessibeSettingsKey')"
          class="
            clickable-list__list-link
            text-mobile-regular-mobile
            text-desktop-regular-desktop
          "
          :class="[
            {
              [$class('active')]: item.label === selected || item.title === selected
            }
          ]"
          data-acsb="trigger"
          @mouseover="() => handleLinkHover(item)"
        >
          <span v-html="item.label ? item.label : item.title" />
        </div>

        <a
          v-else-if="isPrivateSale(item)"
          :href="(item.link ? item.link : item.url)"
          class="
            clickable-list__list-link
            text-mobile-regular-mobile
            text-desktop-regular-desktop
          "
          :class="[
            {
              [$class('active')]: item.label === selected || item.title === selected
            }
          ]"
          @click="(event) => handleLinkClick(event, item)"
          @focus="() => selected = items?.label ? item.label : item.title"
          @mouseover="() => handleLinkHover(item)"
        >
          <span
            v-if="!item.image && !item.video"
          >
            {{ item.label ? item.label : item.title }}
          </span>
        </a>

        <a
          v-else-if="isPublicLink(item)"
          :href="(item.link ? item.link : item.url)"
          class="
            clickable-list__list-link
            text-mobile-regular-mobile
            text-desktop-regular-desktop
          "
          :class="[
            {
              [$class('active')]: item.label === selected || item.title === selected
            }
          ]"
          @click="(event) => handleLinkClick(event, item)"
          @focus="() => selected = items?.label ? item.label : item.title"
          @mouseover="() => handleLinkHover(item)"
        >
          <div
            v-if="(displayIcons || item.showIcon)"
            class="clickable-list__list-icon"
            :class="$blockClass(blockClass, 'list-icon')"
          >
            <span
              v-if="!item.image && !item.video"
              class="clickable-list__list-icon-default"
            >
              <icon-chevron-right-right />
            </span>
          </div>
          <span
            v-if="!item.image && !item.video"
          >
            {{ item.label ? item.label : item.title }}
            <span
              v-show="isNew(item)"
              class="clickable-list__list-link--new"
              v-text="$string('general.site_header.new_flag')"
            />
          </span>

          <div
            v-if="item.image"
            class="clickable-list-image"
          >
            <img
              v-if="item.image"
              :src="item.image"
              alt=""
              loading="lazy"
              class="clickable-list-image__img"
            >
            <div class="clickable-list-image__body">
              <span
                class="clickable-list-image__title"
                :class="{
                  // eslint-disable-next-line max-len
                  'clickable-list-image__title--white': item.links && item.links[0]?.title_raw?.includes('--white')
                }"
                v-html="item.links ? item.links[0]?.title : ''"
              />
              <span
                class="clickable-list-image__icon"
              >
                <icon-chevron-right-right />
              </span>
            </div>
          </div>
          <div
            v-if="item.video"
            class="clickable-list-image"
          >
            <video
              v-if="item.video"
              :src="item.video"
              loading="lazy"
              class="clickable-list-image__img"
              autoplay
              loop
              muted
              playsinline
            />
            <div class="clickable-list-image__body">
              <span
                class="clickable-list-image__title"
                :class="{
                  // eslint-disable-next-line max-len
                  'clickable-list-image__title--white': item.links && item.links[0]?.title_raw?.includes('--white')
                }"
                v-html="item.links ? item.links[0]?.title : ''"
              />
              <span
                class="clickable-list-image__icon"
              >
                <icon-chevron-right-right />
              </span>
            </div>
          </div>
        </a>
        <span
          v-if="item.badge"
          class="clickable-list__badge text-desktop-mini"
          v-html="item.badge"
        />
      </li>
    </component>
  </div>
</template>

<script>

/**
 * Vue: Clickable list (clickable-list)
 * -----------------------------------------------------------------------------
 * List of links with collective styles.
 * @param {String} [blockClass] - String indicating block level BEM class.
 * @param {Array} [links] - Array of link objects, including a label and link
 * key with applicable string values.
 * @param {String} [listType] - Indicates the type of list to render.
 * @param {String} [linkTypes] - Change whether a link triggers an action or
 * routes to a URL. Values are: 'action' or 'url'.
 * @param {Object} [selectedItem] - The currently active item selected at
 * parent.
 *
 */
import IconChevronRightRight from '~icons/direction/chevron/right-right.svg'

import { mapActions, mapState } from 'vuex'
import classes from '~/config/classes'

export default {
  name: 'ClickableList',

  components: {
    IconChevronRightRight,
  },

  props: {
    activateOnHover: {
      type: Boolean,
      default: false,
    },

    blockClass: {
      type: String,
      default: '',
    },

    displayIcons: {
      type: Boolean,
      default: false,
    },

    links: {
      type: Array,
      required: true,
      default: () => [],
      validator: (value) => {
        return Array.isArray(value)
      },
    },

    listType: {
      type: String,
      default: 'ul',
      validator: (value) => {
        return ['ul', 'ol', 'div'].includes(value)
      },
    },

    linkType: {
      type: String,
      default: 'url',
      validator: (value) => {
        return value === 'url' || value === 'action'
      },
    },

    selectedItem: {
      type: Object,
      default: () => null,
    },
  },

  emits: {
    'link-clicked': (payload) => (typeof payload === 'object' &&
      !Array.isArray(payload) &&
      payload !== null) || payload === null,
  },

  data() {
    return {
      anyHover: false,
      selected: null,
      cnvsGlobal: true,
      selectors: {
        cookieContainerDesktop: '[js-onetrust-cookie-settings="cookieContainerDesktop"]',
        cookieButtonDesktop: '[js-onetrust-cookie-settings="cookieButtonDesktop"]',
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns whether to display the list active state.
     * @returns {Boolean}
     */
    linksActive() {
      return this.anyHover || this.selected
    },
  },

  mounted() {
    this.updateOneTrustCookieSettingsButton()
  },

  methods: {

    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Handles mouse enter event.
     */
    handleMouseEnter() {
      if (this.isMobile) {
        return
      }

      this.anyHover = true
    },

    /**
     * Handles mouse leave event.
     */
    handleMouseLeave() {
      if (this.isMobile || !this.activateOnHover) {
        return
      }

      this.anyHover = false
      if (this.selectedItem) {
        return
      }

      this.selected = null
    },

    /**
     * Handles link hover events.
     * @param {Object} event - Mouse input event.
     * @param {Object} item - Link item from for loop.
     */
    handleLinkHover(item) {
      if (this.isMobile || !this.activateOnHover) {
        return
      }

      if (item?.links?.length) {
        this.handleLinkClick(false, item)
        return
      }

      this.selected = item.label ? item.label : item.title
      this.handleLinkClick(false, null)
    },

    /**
     * Handles link clicks.
     * @param {Object} event - Mouse input event.
     * @param {Object} item - Link item from for loop.
     */
    handleLinkClick(event, item) {
      if (!item) {
        this.$emit('link-clicked', item)
        return
      }

      this.handleGtm(event, item.link)

      if (item.link && item.link === 'size-guide') {
        if (event) {
          event.preventDefault()
        }
        this.openOverlay({
          component: 'size-guide',
          ignoreDismissed: true,
          namespace: 'sizeGuide',
        })
        return
      }

      if (this.linkType === 'action' || item.link === '#' || item?.links?.length) {
        if (event) {
          event.preventDefault()
        }

        this.$emit('link-clicked', item)

        // Trigger Zendesk chat
        if (item.label === 'Live Chat') {
          this.selected = window.zE('messenger', 'open')
        }

        this.selected = item.label ? item.label : item.title
      }
    },

    handleGtm(event, itemLink) {
      if (event.target.closest('[data-gtm-event="pdp-secondary-links"]')) {
        this.trackPdpSecondaryLinks(itemLink)
      }

      if (event.target.closest('[data-gtm-mobile-nav]')) {
        this.trackMobileNavDrawerClicks(event)
      }
    },

    trackPdpSecondaryLinks(itemLink) {
      const dataLayer = window.dataLayer || []
      const target = itemLink.replace('-', '_')
      const targetCapitalised = target.charAt(0).toUpperCase() + target.slice(1)
      const event = `pdp_${target}_click`
      const eventDetailsObj = `pdp${targetCapitalised}Click`

      dataLayer.push({
        event,
        [eventDetailsObj]: {
          link_type: 'desktop_link',
          url: document.location.pathname,
        },
      })
    },

    trackMobileNavDrawerClicks(event) {
      const dataLayer = window.dataLayer || []
      const target = event.currentTarget

      if (target.href === document.location.origin) {
        return
      }

      const navLevel = target.closest('[data-gtm-mobile-nav-level]').dataset.gtmMobileNavLevel
      dataLayer.push({
        event: 'navigation_click_mobile',
        navigationClickMobile: {
          level: navLevel,
          click_url: target.href,
          click_text: target.innerText,
        },
      })
    },

    isPrivateSale(item) {
      if (typeof item.title_raw === 'undefined') {
        return false
      }
      return this.privateSale(item.title_raw)
    },

    isNew(item) {
      if (typeof item.title_raw === 'undefined') {
        return false
      }
      return item.title_raw.includes('--new')
    },

    isPublicLink(item) {
      if (typeof item.title_raw === 'undefined') {
        return true
      }
      return !item.title_raw.includes('--private')
    },

    privateSale(title) {
      if (typeof title === 'undefined') {
        return false
      }

      if (title.includes('--private')) {
        if (this.$variable('customer.loggedIn') === true) {
          const customerTier = this.$variable('loyalty.tier')
          if (title.includes(customerTier)) {
            return true
          }
        }
      }
      return false
    },

    /**
     * Utility function to reset component to default state - called by parent.
     */
    resetComponentData() {
      this.selected = null
      this.anyHover = false
    },

    /**
     * Move oneTrust cookie settings button from theme.liquid to Vue instance.
     */
    updateOneTrustCookieSettingsButton() {
      const moveInterval = setInterval(() => {
        const container = document.querySelector(this.selectors.cookieContainerDesktop)
        const button = document.querySelector(this.selectors.cookieButtonDesktop)

        if (!container || !button) {
          return
        }

        clearInterval(moveInterval)
        container.appendChild(button)
        button.classList.remove(classes.hidden)
      }, 500)
    },
  },
}
</script>
